
import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useProducts } from '../hooks/useProducts';
import { GalleryItem } from '../types';
import ProductCard from '../components/ui/ProductCard';
import { useSeo } from '../hooks/useSeo';
import SearchBar from '../components/common/SearchBar';
import { slugify } from '../utils/slugify';
import HeroCarousel from '../components/common/HeroCarousel';
import { calculateDeliveryInfo, getUrgencyWindow } from '../utils/deliveryDateCalculator';

const HomePage: React.FC = () => {
    const { products, helpSettings } = useProducts();

    // --- State for Quick Quote Calculator ---
    const [selectedQuoteProductId, setSelectedQuoteProductId] = useState<number>(0);
    const [quoteQuantityIndex, setQuoteQuantityIndex] = useState(0);
    const [isUrgencyActive, setIsUrgencyActive] = useState(false);
    
    // --- State for Product Filtering ---
    const [activeCategory, setActiveCategory] = useState('Todos');
    const [showAllProducts, setShowAllProducts] = useState(false);

    // --- State for FAQ ---
    const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
    const [lightboxItem, setLightboxItem] = useState<GalleryItem | null>(null);
    const [lightboxIndex, setLightboxIndex] = useState(0);

    const handleWorkClick = (item: GalleryItem) => {
        if (item.projectUrl) {
            window.open(item.projectUrl, '_blank', 'noopener,noreferrer');
        } else {
            setLightboxItem(item);
            setLightboxIndex(0);
        }
    };

    const lightboxImages = lightboxItem
        ? [lightboxItem.imageUrl, ...(lightboxItem.images || [])].filter(Boolean)
        : [];

    const lightboxPrev = () => setLightboxIndex(i => (i - 1 + lightboxImages.length) % lightboxImages.length);
    const lightboxNext = () => setLightboxIndex(i => (i + 1) % lightboxImages.length);

    const closeLightbox = () => { setLightboxItem(null); setLightboxIndex(0); };

    // --- Urgency Logic for Hero Banner ---
    const [timeLeft, setTimeLeft] = useState<{hours: number, minutes: number} | null>(null);

    useEffect(() => {
        // Calculate time left until 3 PM (15:00) cutoff
        const calculateTimeLeft = () => {
            const now = new Date();
            const cutoff = new Date();
            cutoff.setHours(15, 0, 0, 0); // 3:00 PM

            // If it's already past 3 PM, set cutoff for tomorrow
            if (now > cutoff) {
                return null; // Don't show urgent banner if cutoff passed
            }

            const diff = cutoff.getTime() - now.getTime();
            const hours = Math.floor(diff / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

            return { hours, minutes };
        };

        const timer = setInterval(() => {
            setTimeLeft(calculateTimeLeft());
        }, 60000); // Update every minute

        setTimeLeft(calculateTimeLeft());

        return () => clearInterval(timer);
    }, []);


    // Initialize default selection when products load
    React.useEffect(() => {
        if (products.length > 0 && selectedQuoteProductId === 0) {
            setSelectedQuoteProductId(products[0].id);
        }
    }, [products, selectedQuoteProductId]);

    // Reset urgency when product or quantity changes
    useEffect(() => {
        setIsUrgencyActive(false);
    }, [selectedQuoteProductId, quoteQuantityIndex]);

    // Logic for Calculator
    const selectedQuoteProduct = useMemo(() => 
        products.find(p => p.id === selectedQuoteProductId) || products[0]
    , [products, selectedQuoteProductId]);

    const quoteOptions = selectedQuoteProduct?.quantityPricing || [];
    const safeQuoteQuantityIndex = Math.min(quoteQuantityIndex, Math.max(0, quoteOptions.length - 1));
    const currentQuoteOption = quoteOptions[safeQuoteQuantityIndex];

    // Calculate Delivery Data
    const deliveryInfo = useMemo(() => {
        return calculateDeliveryInfo(currentQuoteOption, isUrgencyActive, helpSettings);
    }, [currentQuoteOption, isUrgencyActive, helpSettings]);

    const urgencyWindow = useMemo(() => {
        return getUrgencyWindow(currentQuoteOption);
    }, [currentQuoteOption]);

    const finalPrice = useMemo(() => {
        const base = Number(currentQuoteOption?.price || 0);
        const urgencyCost = (isUrgencyActive && urgencyWindow?.isActive && currentQuoteOption?.urgencyPrice) 
            ? Number(currentQuoteOption.urgencyPrice) 
            : 0;
        return base + urgencyCost;
    }, [currentQuoteOption, isUrgencyActive, urgencyWindow]);

    const deliveryLabel = useMemo(() => {
        if (!deliveryInfo) return currentQuoteOption?.deliveryTime || "Consultar";
        
        const now = new Date();
        const delivery = deliveryInfo.estimatedDeliveryDate;
        
        // Reset hours to compare dates only
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const target = new Date(delivery.getFullYear(), delivery.getMonth(), delivery.getDate());
        
        const diffTime = target.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return "Entrega Hoy";
        if (diffDays === 1) return "Entrega Mañana";
        return `Entrega en ${diffDays} días`;
    }, [deliveryInfo, currentQuoteOption]);


    // Logic for Product Filtering
    const derivedCategories = useMemo(() => {
        const uniqueCats = Array.from(new Set(products.map(p => p.category))).sort();
        return ['Todos', ...uniqueCats];
    }, [products]);

    const filteredProducts = useMemo(() => {
        if (activeCategory === 'Todos') return products;
        return products.filter(p => p.category === activeCategory);
    }, [products, activeCategory]);

    const PRODUCTS_LIMIT = 9;
    const visibleProducts = showAllProducts ? filteredProducts : filteredProducts.slice(0, PRODUCTS_LIMIT);
    const hasMoreProducts = filteredProducts.length > PRODUCTS_LIMIT;

    const handleProductChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedQuoteProductId(Number(e.target.value));
        setQuoteQuantityIndex(0); // Reset quantity slider
    };

    useSeo({
      title: "Impresión Veloz - Imprenta Digital con Entrega Express",
      description: "Impresiones premium listas en horas. Tarjetas, volantes, lonas y más con la rapidez y calidad que tu negocio exige.",
      canonicalUrl: "https://impresionveloz.com/",
      jsonLd: {
          "@context": "https://schema.org",
          "@graph": [
              {
                  "@type": "Organization",
                  "name": "Impresión Veloz",
                  "url": "https://impresionveloz.com",
                  "logo": "https://i.imgur.com/2s4x7x8.png",
                  "contactPoint": {
                      "@type": "ContactPoint",
                      "contactType": "customer service",
                      "availableLanguage": "Spanish"
                  }
              },
              {
                  "@type": "WebSite",
                  "name": "Impresión Veloz",
                  "url": "https://impresionveloz.com",
                  "potentialAction": {
                      "@type": "SearchAction",
                      "target": "https://impresionveloz.com/search?q={search_term_string}",
                      "query-input": "required name=search_term_string"
                  }
              }
          ]
      }
    });

    const recentWorks = helpSettings.recentWorks || [];

    const testimonials = [
        {
            name: "Roberto Méndez",
            role: "Director de Marketing, TechSoft",
            image: "https://randomuser.me/api/portraits/men/32.jpg",
            quote: "Necesitábamos 500 tarjetas para una expo al día siguiente. ImpresiónVeloz no solo cumplió, la calidad superó a nuestro proveedor anterior."
        },
        {
            name: "Carla Pineda",
            role: "Dueña, Cafetería Aroma",
            image: "https://randomuser.me/api/portraits/women/44.jpg",
            quote: "El sistema para subir mi diseño es increíblemente fácil. Mis menús llegaron perfectos y protegidos. Servicio 10/10."
        },
        {
            name: "Alejandro Ruiz",
            role: "Organizador de Eventos",
            image: "https://randomuser.me/api/portraits/men/85.jpg",
            quote: "La consistencia de color en los flyers fue exacta a mi archivo. Es la primera vez que una imprenta online no me decepciona."
        }
    ];

    const faqs = [
        {
            question: "¿Cuánto tiempo tarda la producción?",
            answer: "Depende del producto y la cantidad. La mayoría de nuestros pedidos (tarjetas, volantes) tienen opción de entrega en 24 horas o incluso el mismo día si pides antes de la hora de corte. Al cotizar verás la fecha exacta de entrega."
        },
        {
            question: "¿Revisan mis archivos antes de imprimir?",
            answer: "Sí, contamos con un sistema de verificación automática básica. Además, puedes solicitar una 'Revisión Profesional' por un pequeño costo extra donde un diseñador ajustará sangrados y márgenes."
        },
        {
            question: "¿Qué pasa si no tengo diseño?",
            answer: "¡No hay problema! Puedes usar nuestras plantillas gratuitas, subir tu logo y nosotros lo armamos, o contratar nuestro servicio de 'Diseño a Medida' donde un profesional creará algo único para ti."
        },
        {
            question: "¿Cuáles son los métodos de pago?",
            answer: "Aceptamos tarjetas de crédito/débito (Visa, Mastercard, Amex), PayPal, Mercado Pago y transferencias SPEI. También puedes pagar en efectivo en OXXO."
        },
        {
            question: "¿Hacen envíos a todo México?",
            answer: "Sí, realizamos envíos a toda la República Mexicana. En CDMX y área metropolitana contamos con entregas express locales."
        }
    ];

    return (
        <div className="relative font-sans text-gray-900">
            {/* New Impactful Hero Section */}
            <section className="relative overflow-hidden bg-slate-950 text-white">
                <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-20">
                    <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-600 rounded-full blur-[120px] animate-pulse-slow"></div>
                    <div className="absolute bottom-[-10%] right-[-10%] w-[30%] h-[30%] bg-indigo-500 rounded-full blur-[100px]"></div>
                </div>

                <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 lg:py-32 relative z-10">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div className="text-center lg:text-left space-y-8 animate-fadeIn">
                            
                            {/* Urgency Banner */}
                            {timeLeft && (
                                <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-orange-500 to-red-500 px-4 py-2 rounded-lg mb-4 shadow-lg shadow-orange-900/20 transform -rotate-1 border border-orange-400">
                                    <i className="fas fa-clock text-white animate-bounce-slow"></i>
                                    <span className="text-white text-sm font-bold tracking-wide">
                                        Pide en <span className="bg-white text-orange-600 px-1.5 rounded mx-1">{timeLeft.hours}h {timeLeft.minutes}m</span> para recibir MAÑANA
                                    </span>
                                </div>
                            )}
                            
                            <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight leading-[1.1]">
                                Impresiones <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">Premium</span> a la Velocidad de tu Negocio.
                            </h1>
                            
                            <p className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
                                No esperes días por lo que necesitas hoy. Imprime tus ideas con tecnología de punta y recíbelas en horas, con la calidad impecable que nos define.
                            </p>

                            <div className="max-w-md mx-auto lg:mx-0">
                                <SearchBar variant="hero" />
                            </div>

                            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-5 pt-4">
                                <a 
                                    href="#products" 
                                    className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-bold py-4 px-10 rounded-xl transition-all transform hover:scale-105 shadow-[0_10px_20px_-5px_rgba(37,99,235,0.4)] flex items-center justify-center gap-2"
                                >
                                    Ver Catálogo <i className="fas fa-arrow-right text-sm"></i>
                                </a>
                                <Link 
                                    to="/track" 
                                    className="w-full sm:w-auto bg-white/5 border border-white/10 backdrop-blur-md hover:bg-white/10 text-white font-bold py-4 px-10 rounded-xl transition-colors flex items-center justify-center gap-2"
                                >
                                    <i className="fas fa-truck-loading text-blue-400"></i> Rastrear Pedido
                                </Link>
                            </div>

                            <div className="grid grid-cols-3 gap-4 pt-10 border-t border-white/5">
                                <div className="text-center lg:text-left">
                                    <p className="text-blue-400 font-bold text-xl md:text-2xl tracking-tighter">24h</p>
                                    <p className="text-slate-400 text-[10px] md:text-xs uppercase font-bold tracking-widest">Entrega Máxima</p>
                                </div>
                                <div className="text-center lg:text-left">
                                    <p className="text-blue-400 font-bold text-xl md:text-2xl tracking-tighter">100%</p>
                                    <p className="text-slate-400 text-[10px] md:text-xs uppercase font-bold tracking-widest">Garantizado</p>
                                </div>
                                <div className="text-center lg:text-left">
                                    <p className="text-blue-400 font-bold text-xl md:text-2xl tracking-tighter">API</p>
                                    <p className="text-slate-400 text-[10px] md:text-xs uppercase font-bold tracking-widest">Precios Directos</p>
                                </div>
                            </div>
                        </div>

                        <div className="hidden lg:block relative animate-fadeIn group">
                            <div className="absolute inset-0 bg-blue-500 rounded-[2rem] blur-[60px] opacity-20 group-hover:opacity-30 transition-opacity"></div>
                            <HeroCarousel />
                        </div>
                    </div>
                </div>
            </section>

            {/* How It Works Section */}
            <div className="bg-white pt-24 pb-32 relative -mt-10 rounded-t-[3rem] z-20 shadow-[0_-20px_60px_-15px_rgba(0,0,0,0.3)]">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16 max-w-2xl mx-auto">
                        <span className="text-blue-600 font-bold uppercase tracking-wider text-sm">Proceso Simplificado</span>
                        <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mt-2">Tu pedido listo en 4 pasos</h2>
                        <p className="text-gray-500 mt-4 text-lg">Olvídate de procesos complicados. En Impresión Veloz, priorizamos tu tiempo sin sacrificar calidad.</p>
                    </div>

                    <div className="relative">
                        {/* Connecting Line (Desktop) */}
                        <div className="hidden md:block absolute top-10 left-[12%] right-[12%] h-1 bg-gradient-to-r from-blue-100 via-blue-200 to-blue-100 rounded-full z-0"></div>

                        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative z-10">
                            {/* Step 1: Personaliza */}
                            <div className="flex flex-col items-center text-center group">
                                <div className="w-20 h-20 bg-white border-4 border-blue-50 rounded-full flex items-center justify-center shadow-lg mb-6 transition-transform duration-300 group-hover:-translate-y-2 group-hover:border-blue-100">
                                    <i className="fas fa-sliders-h text-3xl text-indigo-500"></i>
                                </div>
                                <h3 className="text-xl font-bold text-gray-800 mb-2">1. Personaliza</h3>
                                <p className="text-gray-500 text-sm leading-relaxed px-4">Elige cantidad, tipo de papel y acabados. Cotización instantánea.</p>
                            </div>

                            {/* Step 2: Sube tu Diseño */}
                            <div className="flex flex-col items-center text-center group">
                                <div className="w-20 h-20 bg-white border-4 border-blue-50 rounded-full flex items-center justify-center shadow-lg mb-6 transition-transform duration-300 group-hover:-translate-y-2 group-hover:border-blue-100">
                                    <i className="fas fa-cloud-upload-alt text-3xl text-blue-600"></i>
                                </div>
                                <h3 className="text-xl font-bold text-gray-800 mb-2">2. Sube tu Diseño</h3>
                                <p className="text-gray-500 text-sm leading-relaxed px-4">Carga tus archivos PDF, AI o JPG. Si no tienes diseño, pide una replica de diseño o contrata un diseñador.</p>
                            </div>

                            {/* Step 3: Pago Seguro */}
                            <div className="flex flex-col items-center text-center group">
                                <div className="w-20 h-20 bg-white border-4 border-blue-50 rounded-full flex items-center justify-center shadow-lg mb-6 transition-transform duration-300 group-hover:-translate-y-2 group-hover:border-blue-100">
                                    <i className="fas fa-credit-card text-3xl text-purple-500"></i>
                                </div>
                                <h3 className="text-xl font-bold text-gray-800 mb-2">3. Pago Seguro</h3>
                                <p className="text-gray-500 text-sm leading-relaxed px-4">Paga con tarjeta (debito o credito), PayPal, Mercado Pago, transferencia. Proceso 100% encriptado.</p>
                            </div>

                            {/* Step 4: Recibe Express */}
                            <div className="flex flex-col items-center text-center group">
                                <div className="w-20 h-20 bg-white border-4 border-blue-50 rounded-full flex items-center justify-center shadow-lg mb-6 transition-transform duration-300 group-hover:-translate-y-2 group-hover:border-blue-100">
                                    <i className="fas fa-rocket text-3xl text-orange-500"></i>
                                </div>
                                <h3 className="text-xl font-bold text-gray-800 mb-2">4. Recibe Express</h3>
                                <p className="text-gray-500 text-sm leading-relaxed px-4">Producción inmediata y envío rápido a tu domicilio o recolección.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Quick Quote Calculator Section */}
            <section className="relative z-30 -mt-16 px-4 sm:px-6 lg:px-8 mb-24">
                <div className="container mx-auto max-w-6xl">
                    <div className="bg-white rounded-[2.5rem] p-8 md:p-14 shadow-2xl border border-gray-100 relative overflow-hidden">
                        {/* Decorative background blob */}
                        <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-blue-500 via-cyan-400 to-blue-600"></div>
                        <div className="absolute -top-24 -right-24 w-64 h-64 bg-blue-50 rounded-full opacity-50 blur-3xl pointer-events-none"></div>
                        <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-cyan-50 rounded-full opacity-50 blur-3xl pointer-events-none"></div>

                        <div className="text-center mb-10 relative z-10">
                            <span className="inline-block py-1 px-3 rounded-full bg-blue-50 text-blue-600 text-xs font-bold tracking-wide uppercase mb-3">Cotizador Express</span>
                            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900">Cotiza tu Proyecto en 30 Segundos</h2>
                            <p className="text-gray-500 mt-3 text-lg max-w-2xl mx-auto">Calcula el precio exacto al instante, sin registros ni esperas. Elige tu producto y personalízalo.</p>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center relative z-10">
                            <div className="space-y-8">
                                <div>
                                    <label htmlFor="quote-product-select" className="block text-sm font-bold text-gray-700 mb-3 flex items-center">
                                        <i className="fas fa-box-open text-blue-500 mr-2"></i> Selecciona un Producto
                                    </label>
                                    <div className="relative group">
                                        <select
                                            id="quote-product-select"
                                            value={selectedQuoteProductId}
                                            onChange={handleProductChange}
                                            className="block w-full pl-5 pr-12 py-4 text-base md:text-lg border border-gray-200 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 rounded-xl bg-gray-50/50 appearance-none cursor-pointer text-gray-800 font-semibold transition-all hover:bg-white hover:shadow-md"
                                        >
                                            {products.map(p => (
                                                <option key={p.id} value={p.id}>{p.name}</option>
                                            ))}
                                        </select>
                                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-5 text-blue-600">
                                            <i className="fas fa-chevron-down text-sm"></i>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <div className="flex justify-between items-end mb-4">
                                        <label className="block text-sm font-bold text-gray-700 flex items-center">
                                            <i className="fas fa-layer-group text-blue-500 mr-2"></i> Cantidad
                                        </label>
                                        <span className="text-3xl font-extrabold text-blue-600 tracking-tight">
                                            {currentQuoteOption?.quantity || 0} <span className="text-sm font-medium text-gray-400 ml-1">unidades</span>
                                        </span>
                                    </div>
                                    {/* Step Slider — puntos clicables por cantidad */}
                                    <div className="px-2">
                                        <div className="relative">
                                            {/* Track */}
                                            <div className="relative h-2 bg-gray-200 rounded-full">
                                                {/* Barra de progreso */}
                                                <div
                                                    className="absolute h-2 bg-blue-600 rounded-full transition-all duration-300"
                                                    style={{
                                                        width: quoteOptions.length > 1
                                                            ? `${(safeQuoteQuantityIndex / (quoteOptions.length - 1)) * 100}%`
                                                            : '100%'
                                                    }}
                                                />
                                                {/* Puntos / estaciones */}
                                                {quoteOptions.map((opt, idx) => {
                                                    const isActive = idx === safeQuoteQuantityIndex;
                                                    const isPast   = idx < safeQuoteQuantityIndex;
                                                    const pct = quoteOptions.length > 1
                                                        ? (idx / (quoteOptions.length - 1)) * 100
                                                        : 0;
                                                    return (
                                                        <button
                                                            key={idx}
                                                            onClick={() => setQuoteQuantityIndex(idx)}
                                                            title={`${opt.quantity} unidades`}
                                                            className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 rounded-full border-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-1
                                                                ${isActive
                                                                    ? 'w-5 h-5 bg-blue-600 border-blue-600 shadow-lg shadow-blue-400/50 scale-110'
                                                                    : isPast
                                                                        ? 'w-3.5 h-3.5 bg-blue-600 border-blue-600 hover:scale-110'
                                                                        : 'w-3.5 h-3.5 bg-white border-gray-300 hover:border-blue-400 hover:scale-110'
                                                                }`}
                                                            style={{ left: `${pct}%` }}
                                                        />
                                                    );
                                                })}
                                            </div>

                                            {/* Etiquetas de cantidad debajo de cada punto */}
                                            <div className="relative mt-4" style={{ height: '1.2rem' }}>
                                                {quoteOptions.map((opt, idx) => {
                                                    const isActive = idx === safeQuoteQuantityIndex;
                                                    const pct = quoteOptions.length > 1
                                                        ? (idx / (quoteOptions.length - 1)) * 100
                                                        : 0;
                                                    return (
                                                        <button
                                                            key={idx}
                                                            onClick={() => setQuoteQuantityIndex(idx)}
                                                            className={`absolute -translate-x-1/2 text-[11px] font-bold leading-none transition-all duration-200 cursor-pointer whitespace-nowrap
                                                                ${isActive
                                                                    ? 'text-blue-600 scale-110'
                                                                    : 'text-gray-400 hover:text-gray-600'
                                                                }`}
                                                            style={{ left: `${pct}%` }}
                                                        >
                                                            {opt.quantity.toLocaleString('es-MX')}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-2xl p-6 md:p-8 shadow-2xl shadow-blue-900/20 relative overflow-hidden group">
                                <div className="absolute top-0 right-0 w-48 h-48 bg-blue-500 rounded-full filter blur-[80px] opacity-20 group-hover:opacity-30 transition-opacity duration-700"></div>
                                <div className="absolute bottom-0 left-0 w-32 h-32 bg-cyan-400 rounded-full filter blur-[60px] opacity-20 group-hover:opacity-30 transition-opacity duration-700"></div>
                                
                                <div className="relative z-10 flex flex-col h-full justify-between min-h-[280px]">
                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <p className="text-sm text-blue-200 font-bold uppercase tracking-wider">Precio Estimado</p>
                                            
                                            <span className={`bg-white/10 backdrop-blur-md px-3 py-1 rounded-full text-xs font-semibold text-white border border-white/10 flex items-center ${isUrgencyActive ? 'bg-orange-500/20 border-orange-500/50 text-orange-200' : ''}`}>
                                                <i className={`fas ${isUrgencyActive ? 'fa-bolt' : 'fa-clock'} mr-1.5 ${isUrgencyActive ? 'text-orange-400 animate-pulse' : ''}`}></i> 
                                                {deliveryLabel}
                                            </span>
                                        </div>
                                        <div className="flex items-baseline gap-2 mt-2">
                                            <span className="text-5xl md:text-6xl font-extrabold tracking-tight text-white">
                                                ${Number(finalPrice || 0).toLocaleString('es-MX', { minimumFractionDigits: 0 })}
                                            </span>
                                            <span className="text-xl text-blue-200 font-medium">MXN</span>
                                        </div>
                                        <p className="text-slate-400 text-xs mt-1">*IVA no incluido</p>
                                    </div>

                                    {/* Urgency Toggle (Show only if available) */}
                                    {currentQuoteOption && urgencyWindow?.isActive && (
                                        <div className="mt-4 bg-white/5 border border-white/10 rounded-lg p-3 flex items-center justify-between cursor-pointer hover:bg-white/10 transition-colors" onClick={() => setIsUrgencyActive(!isUrgencyActive)}>
                                            <div className="flex items-center gap-2">
                                                <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${isUrgencyActive ? 'border-orange-400 bg-orange-400' : 'border-slate-400'}`}>
                                                    {isUrgencyActive && <i className="fas fa-check text-[10px] text-white"></i>}
                                                </div>
                                                <span className={`text-sm font-semibold ${isUrgencyActive ? 'text-orange-300' : 'text-slate-300'}`}>
                                                    Añadir Urgencia (+${Number(currentQuoteOption.urgencyPrice).toLocaleString('es-MX')})
                                                </span>
                                            </div>
                                            <span className="text-xs text-orange-400 font-bold bg-orange-900/30 px-2 py-0.5 rounded">¡Más Rápido!</span>
                                        </div>
                                    )}

                                    <div className="mt-5 space-y-4">
                                        <div className="grid grid-cols-2 gap-3 text-xs text-slate-300 mb-4">
                                            <div className="flex items-center"><i className="fas fa-check text-green-400 mr-2"></i> Calidad Premium</div>
                                            <div className="flex items-center"><i className="fas fa-check text-green-400 mr-2"></i> Garantía Total</div>
                                            <div className="flex items-center"><i className="fas fa-check text-green-400 mr-2"></i> Revisión de Archivos</div>
                                            <div className="flex items-center"><i className="fas fa-check text-green-400 mr-2"></i> Soporte 24/7</div>
                                        </div>

                                        {selectedQuoteProduct && currentQuoteOption ? (
                                            <Link
                                                to={`/${slugify(selectedQuoteProduct.category)}/${selectedQuoteProduct.seo?.slug}`}
                                                state={{
                                                    quantity: currentQuoteOption.quantity,
                                                    selectedPriceInfo: currentQuoteOption,
                                                    isUrgencyActive: isUrgencyActive
                                                }}
                                                className="block w-full bg-blue-600 hover:bg-blue-500 text-white text-center font-bold py-4 rounded-xl transition-all transform hover:scale-[1.02] shadow-lg shadow-blue-900/50 flex items-center justify-center gap-2 group/btn"
                                            >
                                                Continuar Pedido <i className="fas fa-arrow-right group-hover/btn:translate-x-1 transition-transform"></i>
                                            </Link>
                                        ) : (
                                            <div className="w-full bg-slate-700 text-slate-300 text-center font-semibold py-4 rounded-xl" role="status">
                                                Catálogo temporalmente no disponible
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Products Section */}
            <div id="products" className="bg-slate-50 py-24 border-t border-slate-200">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Section Header */}
                    <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
                        <div className="text-center md:text-left space-y-3">
                            <span className="text-blue-600 font-bold uppercase tracking-wider text-sm">Catálogo Completo</span>
                            <h2 className="text-4xl font-extrabold text-gray-900 tracking-tight">Nuestros Productos Estrella</h2>
                            <p className="text-gray-500 text-lg max-w-xl">Soluciones de impresión profesionales para elevar la imagen de tu marca.</p>
                        </div>
                        
                        {/* Category Filter Tabs */}
                        <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0 w-full md:w-auto hide-scrollbar">
                            {derivedCategories.map(category => (
                                <button
                                    key={category}
                                    onClick={() => { setActiveCategory(category); setShowAllProducts(false); }}
                                    className={`px-5 py-2.5 rounded-full text-sm font-bold transition-all whitespace-nowrap border ${
                                        activeCategory === category 
                                        ? 'bg-blue-600 text-white border-blue-600 shadow-md transform scale-105' 
                                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                                    }`}
                                >
                                    {category}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Products Grid */}
                    {filteredProducts.length > 0 ? (
                        <>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                                {visibleProducts.map(product => (
                                    <ProductCard key={product.id} product={product} />
                                ))}
                            </div>
                            {hasMoreProducts && !showAllProducts && (
                                <div className="text-center mt-12">
                                    <button
                                        onClick={() => setShowAllProducts(true)}
                                        className="inline-flex items-center gap-2 px-8 py-3.5 bg-blue-600 text-white font-bold rounded-full hover:bg-blue-700 transition-all shadow-md hover:shadow-lg transform hover:scale-105"
                                    >
                                        <i className="fas fa-th-large"></i>
                                        Ver catálogo completo ({filteredProducts.length} productos)
                                    </button>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="text-center py-20">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <i className="fas fa-search text-gray-400 text-2xl"></i>
                            </div>
                            <p className="text-gray-500 font-medium">No se encontraron productos en esta categoría.</p>
                            <button onClick={() => setActiveCategory('Todos')} className="mt-4 text-blue-600 font-bold hover:underline">Ver todos los productos</button>
                        </div>
                    )}
                </div>
            </div>

            {/* Testimonials Section */}
            <section className="py-24 bg-white">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900">Lo que dicen nuestros clientes</h2>
                        <p className="text-gray-500 mt-4 text-lg">Más de 500 empresas confían en nuestra velocidad y calidad.</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {testimonials.map((testimonial, index) => (
                            <div key={index} className="bg-slate-50 p-8 rounded-2xl hover:shadow-lg transition-shadow duration-300 border border-slate-100">
                                <div className="flex items-center gap-1 mb-4 text-yellow-400">
                                    <i className="fas fa-star"></i><i className="fas fa-star"></i><i className="fas fa-star"></i><i className="fas fa-star"></i><i className="fas fa-star"></i>
                                </div>
                                <p className="text-gray-700 italic mb-6 leading-relaxed">"{testimonial.quote}"</p>
                                <div className="flex items-center gap-4">
                                    <img src={testimonial.image} alt={testimonial.name} className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm" loading="lazy" decoding="async" width={48} height={48} />
                                    <div>
                                        <h3 className="font-bold text-gray-900 text-sm">{testimonial.name}</h3>
                                        <p className="text-xs text-gray-500 uppercase font-semibold">{testimonial.role}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Recent Work Gallery */}
            <section className="py-24 bg-slate-50">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-end mb-12">
                        <div>
                            <h2 className="text-3xl font-extrabold text-gray-900">Trabajos Recientes</h2>
                            <p className="text-gray-500 mt-2">Explora la calidad de nuestros últimos proyectos.</p>
                        </div>
                        <a href="#" className="hidden md:inline-flex items-center text-blue-600 font-bold hover:text-blue-700">Ver portafolio completo <i className="fas fa-arrow-right ml-2"></i></a>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {recentWorks.map((item, index) => (
                            <div
                                key={item.id || index}
                                onClick={() => handleWorkClick(item)}
                                className="group relative aspect-square overflow-hidden rounded-xl cursor-pointer shadow-sm"
                            >
                                <img src={item.imageUrl} alt={item.title || `Trabajo reciente ${index + 1}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" decoding="async" width={400} height={400} />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                                    <span className="text-white font-bold text-lg translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                                        {item.title || 'Ver Proyecto'}
                                        <i className={`ml-2 text-sm ${item.projectUrl ? 'fas fa-external-link-alt' : 'fas fa-search-plus'}`}></i>
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Lightbox con carrusel */}
                    {lightboxItem && (
                        <div
                            className="fixed inset-0 bg-black/90 z-[200] flex flex-col items-center justify-center p-4"
                            onClick={closeLightbox}
                        >
                            {/* Barra superior: contador + cerrar */}
                            <div className="w-full max-w-4xl flex justify-between items-center mb-3 flex-shrink-0" onClick={e => e.stopPropagation()}>
                                <span className="text-white/60 text-sm font-semibold">
                                    {lightboxImages.length > 1
                                        ? `${lightboxIndex + 1} / ${lightboxImages.length}`
                                        : (lightboxItem.title || '')}
                                </span>
                                <button
                                    onClick={closeLightbox}
                                    className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center text-white transition-all"
                                    aria-label="Cerrar"
                                >
                                    <i className="fas fa-times text-lg"></i>
                                </button>
                            </div>

                            {/* Imagen + flechas laterales */}
                            <div
                                className="relative w-full max-w-4xl flex items-center justify-center"
                                onClick={e => e.stopPropagation()}
                            >
                                {lightboxImages.length > 1 && (
                                    <button
                                        onClick={lightboxPrev}
                                        className="absolute left-2 z-10 w-10 h-10 bg-black/50 hover:bg-black/80 rounded-full flex items-center justify-center text-white transition-all shadow-lg"
                                    >
                                        <i className="fas fa-chevron-left"></i>
                                    </button>
                                )}

                                <img
                                    key={lightboxImages[lightboxIndex]}
                                    src={lightboxImages[lightboxIndex]}
                                    alt={`${lightboxItem.title || 'Proyecto'} — foto ${lightboxIndex + 1}`}
                                    className="max-w-full max-h-[65vh] rounded-xl shadow-2xl object-contain"
                                />

                                {lightboxImages.length > 1 && (
                                    <button
                                        onClick={lightboxNext}
                                        className="absolute right-2 z-10 w-10 h-10 bg-black/50 hover:bg-black/80 rounded-full flex items-center justify-center text-white transition-all shadow-lg"
                                    >
                                        <i className="fas fa-chevron-right"></i>
                                    </button>
                                )}
                            </div>

                            {/* Título */}
                            {lightboxItem.title && (
                                <p
                                    className="text-white font-bold text-center mt-4 text-lg flex-shrink-0"
                                    onClick={e => e.stopPropagation()}
                                >
                                    {lightboxItem.title}
                                </p>
                            )}

                            {/* Puntos indicadores */}
                            {lightboxImages.length > 1 && (
                                <div
                                    className="flex justify-center gap-2 mt-3 flex-shrink-0"
                                    onClick={e => e.stopPropagation()}
                                >
                                    {lightboxImages.map((_, i) => (
                                        <button
                                            key={i}
                                            onClick={() => setLightboxIndex(i)}
                                            className={`w-2 h-2 rounded-full transition-all ${i === lightboxIndex ? 'bg-white scale-125' : 'bg-white/40 hover:bg-white/70'}`}
                                        />
                                    ))}
                                </div>
                            )}

                            {/* Miniaturas */}
                            {lightboxImages.length > 1 && (
                                <div
                                    className="flex gap-2 justify-center mt-3 flex-wrap flex-shrink-0"
                                    onClick={e => e.stopPropagation()}
                                >
                                    {lightboxImages.map((url, i) => (
                                        <button
                                            key={i}
                                            onClick={() => setLightboxIndex(i)}
                                            className={`w-14 h-14 rounded-lg overflow-hidden border-2 transition-all flex-shrink-0 ${i === lightboxIndex ? 'border-white' : 'border-transparent opacity-50 hover:opacity-90'}`}
                                        >
                                            <img src={url} alt={`Miniatura ${i + 1}`} className="w-full h-full object-cover" />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    <div className="mt-8 text-center md:hidden">
                        <a href="#" className="inline-flex items-center text-blue-600 font-bold hover:text-blue-700">Ver portafolio completo <i className="fas fa-arrow-right ml-2"></i></a>
                    </div>
                </div>
            </section>

            {/* FAQ Section (Nuevo) */}
            <section className="py-24 bg-white">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
                    <div className="text-center mb-12">
                         <span className="text-blue-600 font-bold uppercase tracking-wider text-sm">Resolver dudas</span>
                        <h2 className="text-3xl font-extrabold text-gray-900 mt-2">Preguntas Frecuentes</h2>
                    </div>
                    
                    <div className="space-y-4">
                        {faqs.map((faq, index) => (
                            <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                                <button
                                    onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                                    className="w-full flex justify-between items-center p-4 bg-white hover:bg-gray-50 transition-colors text-left"
                                >
                                    <span className="font-bold text-gray-800">{faq.question}</span>
                                    <i className={`fas fa-chevron-down text-gray-400 transition-transform duration-300 ${openFaqIndex === index ? 'rotate-180' : ''}`}></i>
                                </button>
                                <div className={`px-4 text-gray-600 bg-gray-50 transition-all duration-300 ease-in-out overflow-hidden ${openFaqIndex === index ? 'max-h-40 py-4 opacity-100' : 'max-h-0 py-0 opacity-0'}`}>
                                    {faq.answer}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why Choose Us Stats */}
            <section className="py-20 bg-slate-900 text-white">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <h2 className="text-3xl font-bold mb-4">¿Por qué las empresas nos eligen?</h2>
                        <p className="text-slate-400">Combinamos tecnología de vanguardia con un servicio al cliente obsesionado con los detalles.</p>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center divide-x divide-slate-800">
                        <div className="p-4">
                            <div className="text-4xl md:text-5xl font-extrabold text-blue-400 mb-2">500+</div>
                            <div className="text-sm md:text-base text-slate-300">Clientes Corporativos</div>
                        </div>
                        <div className="p-4">
                            <div className="text-4xl md:text-5xl font-extrabold text-blue-400 mb-2">1M+</div>
                            <div className="text-sm md:text-base text-slate-300">Impresiones Entregadas</div>
                        </div>
                        <div className="p-4">
                            <div className="text-4xl md:text-5xl font-extrabold text-blue-400 mb-2">99%</div>
                            <div className="text-sm md:text-base text-slate-300">A Tiempo</div>
                        </div>
                        <div className="p-4">
                            <div className="text-4xl md:text-5xl font-extrabold text-blue-400 mb-2">24/7</div>
                            <div className="text-sm md:text-base text-slate-300">Soporte Online</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Guarantee Banner */}
            <section className="bg-green-600 text-white py-6">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-center gap-4 text-center md:text-left">
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="fas fa-shield-alt text-2xl"></i>
                    </div>
                    <div>
                        <p className="font-bold text-lg">Garantía de Satisfacción Total</p>
                        <p className="text-green-100 text-sm">Si no estás 100% satisfecho con la calidad de impresión, reimprimimos tu pedido o te devolvemos tu dinero.</p>
                    </div>
                </div>
            </section>

            {/* Floating WhatsApp Button */}
            <a 
                href={`https://wa.me/${helpSettings?.whatsappNumber || '5215512345678'}?text=Hola,%20me%20interesa%20imprimir...`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-transform hover:scale-110 animate-bounce-slow group"
                aria-label="Contactar por WhatsApp"
            >
                <i className="fab fa-whatsapp text-3xl"></i>
                <span className="absolute right-full mr-4 bg-white text-gray-800 text-xs font-bold px-3 py-1.5 rounded shadow-md opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                    ¡Hablemos!
                </span>
            </a>
        </div>
    );
};

export default HomePage;
